import { useEffect, useState } from "react";
import { docsClient, Document } from "@/lib/supabase";

// Extended type for documents with new token-related fields
// These fields are added by migration 20251218_add_token_limit_columns.sql
type DocumentWithTokens = Document & {
  estimated_tokens?: number | null;
  last_cleaned_at?: string | null;
  cleaning_algorithm_version?: string | null;
  chunk_count?: number | null;
};
import { useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  ExternalLink,
  Trash2,
  Tag as TagIcon,
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileWarning,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import AddDocumentDialog from "@/components/documents/AddDocumentDialog";
import DocumentActionsMenu from "@/components/documents/DocumentActionsMenu";
import BulkTagDialog from "@/components/documents/BulkTagDialog";
import ViewDocumentDialog from "@/components/documents/ViewDocumentDialog";
import EmptyState from "@/components/EmptyState";

const PAGE_SIZE = 50;

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState<DocumentWithTokens[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentWithTokens[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [viewDocumentId, setViewDocumentId] = useState<string | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const { toast } = useToast();

  // Handle tag query parameter from URL
  useEffect(() => {
    const tagParam = searchParams.get("tag");
    if (tagParam) {
      setSelectedTag(tagParam);
    }
  }, [searchParams]);

  // Handle id query parameter for viewing document
  useEffect(() => {
    const idParam = searchParams.get("id");
    if (idParam && documents.some((doc) => doc.id === idParam)) {
      setViewDocumentId(idParam);
      setViewOpen(true);
    } else if (!idParam) {
      setViewDocumentId(null);
      setViewOpen(false);
    }
  }, [searchParams, documents]);

  useEffect(() => {
    loadDocuments();
  }, [currentPage, searchQuery, selectedTag]);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery, selectedTag]);

  const loadDocuments = async () => {
    try {
      setLoading(true);

      // Build query with server-side filtering
      const from = (currentPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = docsClient.from("documents").select("*", { count: "exact" });

      // Apply search filter (server-side using indexed columns)
      if (searchQuery) {
        const searchPattern = `%${searchQuery}%`;
        query = query.or(
          `title.ilike.${searchPattern},description.ilike.${searchPattern},url.ilike.${searchPattern}`
        );
      }

      // Apply tag filter (server-side using GIN index on tags array)
      if (selectedTag) {
        query = query.contains("tags", [selectedTag]);
      }

      // Apply sorting and pagination
      query = query.order("created_at", { ascending: false }).range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      setDocuments(data || []);
      setFilteredDocuments(data || []);
      setTotalCount(count || 0);
      setSelectedIds(new Set()); // Clear selection on reload
    } catch (error) {
      console.error("Error loading documents:", error);
      toast({
        title: "Error",
        description: "Failed to load documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Note: Filtering is now done server-side in loadDocuments()
  // This function is no longer needed but kept for backwards compatibility
  const filterDocuments = () => {
    // Server-side filtering handles this now
    setFilteredDocuments(documents);
  };

  const getAllTags = () => {
    const tagsSet = new Set<string>();
    documents.forEach((doc) => {
      doc.tags.forEach((tag) => tagsSet.add(tag));
    });
    return Array.from(tagsSet).sort();
  };

  const formatBytes = (bytes: number | null) => {
    if (!bytes) return "N/A";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatTokens = (tokens: number | null) => {
    if (!tokens) return null;
    const tokenLimit = 25000; // Claude Code Read tool limit
    if (tokens > tokenLimit) {
      return `~${(tokens / 1000).toFixed(1)}K tokens (exceeds ${tokenLimit / 1000}K limit)`;
    }
    return `~${(tokens / 1000).toFixed(1)}K tokens`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="outline" className="text-xs gap-1">
            <AlertCircle className="h-3 w-3" />
            Pending
          </Badge>
        );
      case "processing":
        return (
          <Badge
            variant="outline"
            className="text-xs gap-1 border-blue-500 text-blue-700 dark:text-blue-400"
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            Processing
          </Badge>
        );
      case "complete":
        return (
          <Badge
            variant="outline"
            className="text-xs gap-1 border-green-500 text-green-700 dark:text-green-400"
          >
            <CheckCircle2 className="h-3 w-3" />
            Complete
          </Badge>
        );
      case "failed":
        return (
          <Badge
            variant="outline"
            className="text-xs gap-1 border-red-500 text-red-700 dark:text-red-400"
          >
            <XCircle className="h-3 w-3" />
            Failed
          </Badge>
        );
      case "too_large":
        return (
          <Badge
            variant="outline"
            className="text-xs gap-1 border-amber-500 text-amber-700 dark:text-amber-400"
          >
            <FileWarning className="h-3 w-3" />
            Too Large
          </Badge>
        );
      default:
        return null;
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredDocuments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDocuments.map((doc) => doc.id)));
    }
  };

  const toggleSelectDocument = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);

    try {
      const idsArray = Array.from(selectedIds);
      const { error } = await docsClient.from("documents").delete().in("id", idsArray);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Deleted ${idsArray.length} document(s)`,
      });

      setBulkDeleteDialogOpen(false);
      loadDocuments();
    } catch (error) {
      console.error("Error deleting documents:", error);
      toast({
        title: "Error",
        description: "Failed to delete documents",
        variant: "destructive",
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Documents</h1>
          <p className="text-muted-foreground mt-2">
            {totalCount} total documents · Page {currentPage} of{" "}
            {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
            {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
          </p>
        </div>
        <AddDocumentDialog onDocumentAdded={loadDocuments} />
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 p-4 bg-primary/10 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium text-foreground">
            {selectedIds.size} document(s) selected
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setBulkTagDialogOpen(true)}>
              <TagIcon className="mr-2 h-4 w-4" />
              Update Tags
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkDeleteDialogOpen(true)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search documents by title, description, URL, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tag Filters */}
      {getAllTags().length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedTag === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedTag(null)}
          >
            All Tags
          </Button>
          {getAllTags().map((tag) => (
            <Button
              key={tag}
              variant={selectedTag === tag ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedTag(tag)}
            >
              {tag}
            </Button>
          ))}
        </div>
      )}

      {/* Mobile Card View */}
      <div className="block lg:hidden space-y-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))
        ) : filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="p-8">
              <EmptyState
                icon={<FileText className="h-12 w-12" />}
                title="No documents found"
                description={
                  searchQuery || selectedTag
                    ? "No documents match your filters"
                    : 'No documents yet. Click "Add Document" to get started.'
                }
              />
            </CardContent>
          </Card>
        ) : (
          filteredDocuments.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Checkbox
                      checked={selectedIds.has(doc.id)}
                      onCheckedChange={() => toggleSelectDocument(doc.id)}
                      aria-label={`Select ${doc.title || doc.url}`}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate text-foreground">
                        {doc.title || <span className="text-muted-foreground">Untitled</span>}
                      </h3>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1 mt-1 truncate"
                      >
                        <span className="truncate">{doc.url.substring(0, 40)}...</span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    </div>
                  </div>
                  <DocumentActionsMenu document={doc} onDocumentUpdated={loadDocuments} />
                </div>
                {doc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {doc.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getStatusBadge(doc.processing_status)}
                    <span>{formatBytes(doc.size)}</span>
                    {doc.estimated_tokens && doc.processing_status === "too_large" && (
                      <Badge
                        variant="outline"
                        className="text-xs border-amber-500 text-amber-700 dark:text-amber-400"
                      >
                        {formatTokens(doc.estimated_tokens)}
                      </Badge>
                    )}
                  </div>
                  <span>{formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block rounded-md border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={
                    filteredDocuments.length > 0 && selectedIds.size === filteredDocuments.length
                  }
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>Title</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Tokens</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Loading skeletons
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-4" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-64" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-8" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  {searchQuery || selectedTag
                    ? "No documents match your filters"
                    : 'No documents yet. Click "Add Document" to get started.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredDocuments.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(doc.id)}
                      onCheckedChange={() => toggleSelectDocument(doc.id)}
                      aria-label={`Select ${doc.title || doc.url}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium text-foreground">
                    {doc.title || <span className="text-muted-foreground">Untitled</span>}
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1"
                    >
                      {doc.url}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {doc.tags.length > 0 ? (
                        doc.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">No tags</span>
                      )}
                      {doc.tags.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{doc.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(doc.processing_status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatBytes(doc.size)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {doc.estimated_tokens ? (
                      doc.processing_status === "too_large" ? (
                        <Badge
                          variant="outline"
                          className="border-amber-500 text-amber-700 dark:text-amber-400"
                        >
                          {formatTokens(doc.estimated_tokens)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {formatTokens(doc.estimated_tokens)}
                        </span>
                      )
                    ) : (
                      <span className="text-muted-foreground">N/A</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <DocumentActionsMenu document={doc} onDocumentUpdated={loadDocuments} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Bulk Tag Dialog */}
      <BulkTagDialog
        open={bulkTagDialogOpen}
        onOpenChange={setBulkTagDialogOpen}
        selectedIds={Array.from(selectedIds)}
        onTagsUpdated={loadDocuments}
      />

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Documents</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} document(s)? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? "Deleting..." : "Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Document Dialog */}
      {viewDocumentId && documents.find((doc) => doc.id === viewDocumentId) && (
        <ViewDocumentDialog
          document={documents.find((doc) => doc.id === viewDocumentId)!}
          open={viewOpen}
          onOpenChange={(open) => {
            setViewOpen(open);
            if (!open) {
              setViewDocumentId(null);
              navigate("/documents", { replace: true });
            }
          }}
        />
      )}

      {/* Pagination Controls */}
      {!loading && totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <div className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
            {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} documents
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, Math.ceil(totalCount / PAGE_SIZE)) }, (_, i) => {
                const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                let pageNum: number;

                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="min-w-[40px]"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((p) => Math.min(Math.ceil(totalCount / PAGE_SIZE), p + 1))
              }
              disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE)}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
